/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DeviceMobileIcon, XIcon, ShareNetworkIcon } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

/**
 * Install PWA prompt. Shows a floating card when the browser fires beforeinstallprompt.
 * On iOS Safari (no event), shows a manual tutorial via "Install" button in nav / landing.
 */
const InstallPWA = () => {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIos, setShowIos] = useState(false);
  const isIos = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone =
    typeof window !== "undefined" &&
    (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);

  useEffect(() => {
    if (standalone) return;
    if (localStorage.getItem("golucru_install_dismissed") === "1") return;
    const h = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", h);
    // On iOS, auto-show the tutorial after 3s if not standalone
    if (isIos) {
      const t = setTimeout(() => setVisible(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", h);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", h);
  }, [isIos, standalone]);

  if (standalone) return null;

  const dismiss = () => {
    localStorage.setItem("golucru_install_dismissed", "1");
    setVisible(false);
    setShowIos(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome !== "dismissed") dismiss();
      setDeferred(null);
      setVisible(false);
    } else if (isIos) {
      setShowIos(true);
    }
  };

  return (
    <>
      <AnimatePresence>
        {visible && !showIos && (
          <motion.div
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:w-96 glass rounded-2xl p-4 flex items-center gap-3 z-50 glow-primary"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            data-testid="pwa-install-banner"
          >
            <div className="w-10 h-10 shrink-0">
              <Logo size={40} withBackground />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Install LUCRÜ</div>
              <div className="text-[11px] text-white/60 truncate">Add to home screen for fullscreen mode</div>
            </div>
            <button
              onClick={install}
              className="btn-pill btn-primary px-3 py-1.5 text-xs shrink-0"
              data-testid="pwa-install-button"
            >
              Install
            </button>
            <button onClick={dismiss} className="text-white/40 hover:text-white shrink-0" data-testid="pwa-dismiss">
              <XIcon size={16} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showIos && (
          <motion.div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-end md:items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={dismiss}
          >
            <motion.div
              className="bg-[#0F0F16] border border-[#222233] rounded-2xl p-6 max-w-sm w-full"
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="font-display font-black text-lg">Install on iOS</div>
                <button onClick={dismiss}><XIcon size={18} /></button>
              </div>
              <ol className="space-y-3 text-sm text-white/80">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center text-[#00E5FF] text-xs shrink-0">1</span>
                  <span>Tap <ShareNetworkIcon size={14} className="inline" /> <strong>Share</strong> in Safari's toolbar</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center text-[#00E5FF] text-xs shrink-0">2</span>
                  <span>Scroll down and tap <strong>Add to Home Screen</strong></span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#00E5FF]/10 border border-[#00E5FF]/30 flex items-center justify-center text-[#00E5FF] text-xs shrink-0">3</span>
                  <span>Open <strong>LUCRÜ</strong> from your home screen like any app</span>
                </li>
              </ol>
              <button onClick={dismiss} className="btn-pill btn-ghost w-full justify-center mt-5" data-testid="pwa-ios-close">Got it</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default InstallPWA;
